/**
 * Google tag manager integration
 */

import type { ValueOf } from 'type-fest';

import omit from 'lodash/omit';

import trackerConstants from './constants';
import { trackEvent } from './dataLayer';
import eventProperties from './event/properties';
import getPage from './routesMapping';

const { BOOKING_GTM_MAPPING_V2, EVENTS, VV_EVENTS_ROOT_PREFIX } = trackerConstants;

export type TrackerActions = ValueOf<typeof trackerConstants.EVENT_ACTIONS>;

export type TrackerOptions = {
  action: TrackerActions;
  category: string;
  event: string;
  flyouts: string[];
  label: string;
  location: string;
  module: string;
  prefix: string;
  urlMap: Record<string, string>;
};

const nonForwardableOptions = [
  'event',
  'location',
  'module',
  'actions',
  'label',
  'action',
  'category',
  'flyouts',
  'urlMap',
  'prefix',
] as const;
type NonForwardableOptions = (typeof nonForwardableOptions)[number];

/*
 * trackEvent() functions factory with defaults
 * Ex.:
 * const trackEvent = createTracker({
 *    prefix: trackerConstants.PREFIXES.PREFIX_1,
 *    event: trackerConstants.EVENTS.EVENT_1,
 *    module: trackerConstants.MODULES.MODULE_1,
 *    flyouts: [trackerConstants.FLYOUTS.FLYOUT_1, trackerConstants.FLYOUTS.FLYOUT_2],
 *    location: 'any_page_name',
 *    label:  trackerConstants.EVENT_LABELS.LABEL_1,
 * });
 * trackEvent({ action: trackerConstants.EVENT_ACTIONS.ACTION_1 });
 * will generate
 * {
 *   event: 'event-1',
 *   eventCategory: 'prefix_1-any_page_name|module_1|flyout_1-flyout_2',
 *   eventAction: 'action_1',
 *   eventLabel: 'label_1'
 * }
 */
export const createTracker = (defaultOptions: Partial<TrackerOptions>) => (options: Partial<TrackerOptions>) => {
  try {
    const {
      action,
      category,
      event = EVENTS.GTMevent,
      flyouts,
      label = '',
      location: urlPath,
      module,
      prefix = VV_EVENTS_ROOT_PREFIX,
      urlMap = BOOKING_GTM_MAPPING_V2,
    } = { ...defaultOptions, ...options };

    if (!action) {
      throw new Error("Tracker::trackGtmEvent: required param 'action' is missing");
    }

    const eventCategory =
      category ||
      eventProperties.getEventCategory({
        flyouts,
        module,
        prefix,
        urlMap,
        urlPath,
      });

    const trackableEvent = {
      event,
      eventAction: action,
      eventCategory,
      eventLabel: label,
      ...(omit(options, nonForwardableOptions) as Omit<TrackerOptions, NonForwardableOptions>),
    };

    trackEvent(trackableEvent);
  } catch (err) {
    console.error('createTracker failed to track event', err);
  }
};

/**
 * General function to track events
 * @deprecated use createTracker instead
 */
export const commonTracking = (eventAction: string, eventLabel: string, trackerEventCategory: string) => {
  const page = getPage();
  trackEvent({
    event: 'GTMevent',
    eventAction,
    eventCategory: trackerEventCategory && trackerEventCategory.length ? trackerEventCategory : `VV-${page}`,
    eventLabel,
  });
};
